import React, { useEffect, useRef, useState } from "react";
import { X, UserPlus, Check, Send, Users, MessageCircle, ArrowLeft } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

// Render URLs inside text as clickable links (owner-sent review links, etc.)
function renderMsgWithLinks(text) {
  const parts = String(text || "").split(/(https?:\/\/\S+|www\.\S+)/g);
  return parts.map((p, i) => {
    if (/^(https?:\/\/|www\.)/i.test(p)) {
      const href = p.startsWith("http") ? p : `https://${p}`;
      return <a key={i} href={href} target="_blank" rel="noopener noreferrer" className="underline font-bold break-all">{p}</a>;
    }
    return <span key={i}>{p}</span>;
  });
}

export default function ChatHub({ slug, guest, onClose }) {
  // Global chat state
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const bottomRef = useRef(null); const sinceRef = useRef(null);

  // DM state
  const [active, setActive] = useState([]);
  const [invites, setInvites] = useState([]);
  const [convs, setConvs] = useState([]);
  const [dmMsgs, setDmMsgs] = useState([]);
  const [openConv, setOpenConv] = useState(null);
  const [dmText, setDmText] = useState("");
  const dmSinceRef = useRef(null);
  const dmBottomRef = useRef(null);

  const [tab, setTab] = useState("general"); // general | people
  const [unreadPeople, setUnreadPeople] = useState(0);
  const lastInvitesCountRef = useRef(0);

  // Poll global chat
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/public/menu/${slug}/chat${sinceRef.current ? `?since=${encodeURIComponent(sinceRef.current)}` : ""}`);
        if (!alive) return;
        if (data.messages.length) {
          setMsgs((prev) => {
            const seen = new Set(prev.map((m) => m.id));
            const merged = [...prev, ...data.messages.filter((m) => !seen.has(m.id))];
            if (merged.length) sinceRef.current = merged[merged.length - 1].ts;
            return merged;
          });
        }
      } catch {}
    };
    load(); const t = setInterval(load, 3000); return () => { alive = false; clearInterval(t); };
  }, [slug]);

  // Poll presence + DMs
  const pollDM = async () => {
    try {
      api.post(`/public/menu/${slug}/presence`, { guest_id: guest.guest_id }).catch(() => {});
      const [{ data: p }, { data: d }] = await Promise.all([
        api.get(`/public/menu/${slug}/presence`),
        api.get(`/public/menu/${slug}/dm?guest_id=${guest.guest_id}${dmSinceRef.current ? `&since=${encodeURIComponent(dmSinceRef.current)}` : ""}`),
      ]);
      setActive(p.active.filter((g) => g.id !== guest.guest_id));
      setInvites(d.invites); setConvs(d.conversations);
      if (d.messages.length) {
        setDmMsgs((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          const merged = [...prev, ...d.messages.filter((m) => !seen.has(m.id))];
          if (merged.length) dmSinceRef.current = merged[merged.length - 1].ts;
          return merged;
        });
      }
      // Track unread invites when not viewing people tab
      if (tab !== "people" && d.invites.length > lastInvitesCountRef.current) {
        setUnreadPeople((c) => c + (d.invites.length - lastInvitesCountRef.current));
      }
      lastInvitesCountRef.current = d.invites.length;
    } catch {}
  };
  useEffect(() => { pollDM(); const t = setInterval(pollDM, 3000); return () => clearInterval(t); }, [slug]);
  useEffect(() => { if (tab === "people") setUnreadPeople(0); }, [tab]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  useEffect(() => { dmBottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [dmMsgs, openConv]);

  const sendGlobal = async () => {
    const t = text.trim();
    if (!t) return;
    const low = t.toLowerCase();
    if (low.includes("http://") || low.includes("https://") || low.includes("www.")) {
      toast.error("Solo il locale può inviare link nella chat");
      return;
    }
    try { await api.post(`/public/menu/${slug}/chat`, { guest_id: guest.guest_id, message: text }); setText(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore invio"); }
  };
  const invite = async (toId) => {
    try { await api.post(`/public/menu/${slug}/dm/invite`, { from_guest_id: guest.guest_id, to_guest_id: toId }); toast.success("Invito inviato"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const respond = async (invite_id, accept) => {
    try {
      const { data } = await api.post(`/public/menu/${slug}/dm/respond`, { invite_id, accept });
      pollDM();
      if (accept && data.conversation_id) setOpenConv(data.conversation_id);
    } catch { toast.error("Errore"); }
  };
  const sendDM = async () => {
    if (!dmText.trim() || !openConv) return;
    try { await api.post(`/public/menu/${slug}/dm/message`, { conversation_id: openConv, guest_id: guest.guest_id, message: dmText }); setDmText(""); pollDM(); }
    catch { toast.error("Errore"); }
  };

  const currentConv = convs.find((c) => c.id === openConv);
  const currentMsgs = dmMsgs.filter((m) => m.conversation_id === openConv);

  // --- Conversation view (1-to-1) ---
  if (openConv && currentConv) {
    return (
      <div className="fixed inset-0 z-50 bg-[#FBF9F5] flex flex-col">
        <div className="border-b border-[#E8E2D5] px-5 py-3 flex items-center gap-3">
          <button data-testid="dm-back-btn" onClick={() => setOpenConv(null)} className="w-9 h-9 rounded-full bg-[#F0EBE1] grid place-items-center"><ArrowLeft size={16} className="text-[#231F1D]"/></button>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-[#C85A32] font-bold">{currentConv.other.is_owner ? "Chat con lo staff" : "Chat privata"}</div>
            <div className="text-sm text-[#231F1D] font-semibold flex items-center gap-1.5">{currentConv.other.name}{currentConv.other.table_number && ` · T${currentConv.other.table_number}`}{currentConv.other.is_owner && <span className="text-[9px] font-black text-[#8C580C] bg-[#FEF3D6] border border-[#F4E3B8] px-1.5 py-0.5 rounded-full uppercase tracking-widest">🍴 STAFF</span>}</div>
          </div>
          <button data-testid="chat-close-btn" onClick={onClose} className="w-9 h-9 rounded-full bg-[#231F1D] grid place-items-center hover:bg-black transition"><X size={18} className="text-white"/></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {currentMsgs.length === 0 && <div className="text-center text-[#6B625B] mt-10 text-sm">Nessun messaggio ancora. Saluta!</div>}
          {currentMsgs.map((m) => {
            const mine = m.guest_id === guest.guest_id;
            const isStaff = m.is_owner;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${mine ? "bg-[#C85A32] text-white" : isStaff ? "bg-gradient-to-r from-[#D99B26] to-[#C85A32] text-white" : "bg-white border border-[#E8E2D5]"}`}>
                  {!mine && isStaff && <div className="text-[10px] font-black uppercase tracking-widest">🍴 STAFF</div>}
                  <div className="text-sm break-words">{renderMsgWithLinks(m.message)}</div>
                </div>
              </div>
            );
          })}
          <div ref={dmBottomRef}/>
        </div>
        <div className="border-t border-[#E8E2D5] p-3 flex gap-2 bg-white">
          <input data-testid="dm-input" value={dmText} onChange={(e) => setDmText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendDM()} placeholder="Scrivi in privato..." className="flex-1 rounded-full border border-[#E8E2D5] px-4 py-2 text-sm"/>
          <button data-testid="dm-send-btn" onClick={sendDM} className="w-10 h-10 bg-[#C85A32] text-white rounded-full grid place-items-center"><Send size={16}/></button>
        </div>
      </div>
    );
  }

  // --- Hub view (Generale / Persone) ---
  const peopleBadge = unreadPeople + invites.length;

  return (
    <div className="fixed inset-0 z-50 bg-[#FBF9F5] flex flex-col">
      <div className="border-b border-[#E8E2D5] px-5 py-3 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#C85A32] font-bold">Chat del locale</div>
          <div className="text-sm text-[#6B625B]">Sei {guest.name}{guest.table_number && ` · Tavolo ${guest.table_number}`}</div>
        </div>
        <button data-testid="chat-close-btn" onClick={onClose} className="w-9 h-9 rounded-full bg-[#231F1D] grid place-items-center hover:bg-black transition"><X size={18} className="text-white"/></button>
      </div>

      {/* Tabs */}
      <div className="border-b border-[#E8E2D5] bg-white flex">
        <button data-testid="chat-tab-general" onClick={() => setTab("general")} className={`flex-1 py-3 text-sm font-bold flex items-center justify-center gap-2 ${tab === "general" ? "text-[#C85A32] border-b-2 border-[#C85A32]" : "text-[#6B625B]"}`}>
          <MessageCircle size={16}/> Generale
        </button>
        <button data-testid="chat-tab-people" onClick={() => setTab("people")} className={`flex-1 py-3 text-sm font-bold flex items-center justify-center gap-2 relative ${tab === "people" ? "text-[#C85A32] border-b-2 border-[#C85A32]" : "text-[#6B625B]"}`}>
          <Users size={16}/> Persone <span className="text-xs opacity-70">({active.length})</span>
          {peopleBadge > 0 && tab !== "people" && (
            <span className="absolute top-1.5 right-4 bg-[#C85A32] text-white text-[10px] min-w-[18px] h-[18px] px-1 rounded-full grid place-items-center font-bold">{peopleBadge}</span>
          )}
        </button>
      </div>

      {tab === "general" ? (
        <>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {msgs.length === 0 && <div className="text-center text-[#6B625B] mt-10 text-sm">Ancora nessun messaggio. Rompi il ghiaccio!</div>}
            {msgs.map((m) => {
              const mine = m.guest_id === guest.guest_id;
              const isStaff = m.is_owner;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${mine ? "bg-[#C85A32] text-white" : isStaff ? "bg-gradient-to-r from-[#FEF3D6] to-[#FDECE4] border-2 border-[#D99B26] shadow-sm" : "bg-white border border-[#E8E2D5]"}`}>
                    {!mine && (
                      isStaff ? (
                        <div className="text-[10px] font-black text-[#8C580C] uppercase tracking-widest inline-flex items-center gap-1">🍴 STAFF · {m.guest_name}</div>
                      ) : (
                        <div className="text-[10px] font-bold text-[#3B503A] uppercase tracking-wider">{m.guest_name}{m.table_number && ` · T${m.table_number}`}</div>
                      )
                    )}
                    <div className={`text-sm break-words ${isStaff && !mine ? "text-[#231F1D] font-semibold" : ""}`}>{renderMsgWithLinks(m.message)}</div>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef}/>
          </div>
          <div className="border-t border-[#E8E2D5] p-3 flex gap-2 bg-white">
            <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendGlobal()} placeholder="Scrivi un messaggio..." className="flex-1 rounded-full border border-[#E8E2D5] px-4 py-2 text-sm"/>
            <button data-testid="chat-send-btn" onClick={sendGlobal} className="w-10 h-10 bg-[#C85A32] text-white rounded-full grid place-items-center"><Send size={16}/></button>
          </div>
        </>
      ) : (
        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {invites.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#8C580C] mb-2">Inviti ricevuti</div>
              <div className="space-y-2">{invites.map((i) => (
                <div key={i.id} data-testid={`dm-invite-${i.id}`} className="rounded-2xl bg-[#FEF3D6] border border-[#F4E3B8] p-3 flex items-center gap-3">
                  <div className="flex-1"><div className="font-semibold text-sm">{i.from_name}{i.from_table && ` · T${i.from_table}`}</div><div className="text-xs text-[#8C580C]">Vuole chattare in privato</div></div>
                  <button data-testid={`dm-accept-${i.id}`} onClick={() => respond(i.id, true)} className="w-9 h-9 rounded-full bg-[#3B503A] text-white grid place-items-center"><Check size={14}/></button>
                  <button data-testid={`dm-reject-${i.id}`} onClick={() => respond(i.id, false)} className="w-9 h-9 rounded-full bg-white border border-[#E8E2D5] grid place-items-center"><X size={14}/></button>
                </div>
              ))}</div>
            </div>
          )}

          {convs.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#3B503A] mb-2">Le tue chat private</div>
              <div className="space-y-2">{convs.map((c) => (
                <button key={c.id} data-testid={`dm-conv-${c.id}`} onClick={() => setOpenConv(c.id)} className={`w-full text-left rounded-2xl border p-3 flex items-center gap-3 hover:border-[#C85A32] transition ${c.other.is_owner ? "bg-gradient-to-r from-[#FEF3D6] to-[#FDECE4] border-[#D99B26]" : "bg-white border-[#E8E2D5]"}`}>
                  <div className={`w-10 h-10 rounded-full grid place-items-center font-bold text-white ${c.other.is_owner ? "bg-gradient-to-br from-[#D99B26] to-[#C85A32]" : "bg-[#C85A32]"}`}>{c.other.is_owner ? "🍴" : (c.other.name[0]?.toUpperCase())}</div>
                  <div className="flex-1">
                    <div className="font-semibold text-sm flex items-center gap-1.5">{c.other.name}{c.other.table_number && ` · T${c.other.table_number}`}{c.other.is_owner && <span className="text-[9px] font-black text-[#8C580C] bg-[#FEF3D6] border border-[#F4E3B8] px-1.5 py-0.5 rounded-full uppercase tracking-widest">STAFF</span>}</div>
                    <div className="text-xs text-[#6B625B]">{c.other.is_owner ? "Il locale ti ha scritto — tocca per rispondere" : "Tocca per aprire"}</div>
                  </div>
                </button>
              ))}</div>
            </div>
          )}

          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#6B625B] mb-2 flex items-center gap-1"><Users size={11}/> Persone online</div>
            {active.length === 0 ? (
              <div className="text-sm text-[#6B625B] italic bg-white border border-[#E8E2D5] rounded-2xl p-4 text-center">Sei l'unico attivo al momento. Aspetta che qualcuno si unisca!</div>
            ) : (
              <div className="space-y-2">{active.map((g) => {
                const hasConv = convs.some((c) => c.other.guest_id === g.id);
                return (
                  <div key={g.id} data-testid={`dm-user-${g.id}`} className="rounded-2xl bg-white border border-[#E8E2D5] p-3 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#EAF0E9] text-[#3B503A] grid place-items-center font-bold relative">
                      {g.name[0]?.toUpperCase()}
                      <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-[#3B503A] border-2 border-white"/>
                    </div>
                    <div className="flex-1"><div className="font-semibold text-sm">{g.name}</div>{g.table_number && <div className="text-xs text-[#6B625B]">Tavolo {g.table_number}</div>}</div>
                    {hasConv ? (
                      <button data-testid={`dm-open-${g.id}`} onClick={() => { const c = convs.find((c) => c.other.guest_id === g.id); if (c) setOpenConv(c.id); }} className="text-xs text-[#3B503A] font-semibold px-3 py-1.5 rounded-full bg-[#EAF0E9]">Apri chat</button>
                    ) : (
                      <button data-testid={`dm-invite-btn-${g.id}`} onClick={() => invite(g.id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#C85A32] text-white text-xs font-bold hover:brightness-110 transition"><UserPlus size={12}/> Invita</button>
                    )}
                  </div>
                );
              })}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
